import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch work history preferences from the API
 * Returns list of organizations with their saved bullet preferences
 */
export const useSavedOrgPreferences = (enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["workHistoryPreferences"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      
      console.log("[Preferences] Fetching preferences...", { token: token ? "✓" : "✗" });
      
      if (!token) {
        throw new Error("No authentication token");
      }

      const response = await fetch("/api/work-history/preferences", {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      console.log("[Preferences] API Response:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[Preferences] API Error:", errorText);
        throw new Error(`Failed to fetch preferences: ${response.status}`);
      }

      const result = await response.json();
      console.log("[Preferences] Data received:", result);
      return result;
    },
    enabled,
    staleTime: 0, // Always refetch when modal opens
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const savedPreferences = useMemo(() => {
    if (!data) {
      return [];
    }

    // Handle response structure: { preferences: [...] }
    const preferencesArray = data.preferences || data;
    
    if (!Array.isArray(preferencesArray)) {
      console.warn("[Preferences] Unexpected data format:", data);
      return [];
    }

    // Transform API response to component format
    return preferencesArray.map((item) => ({
      workHistoryId: item.workHistoryId,
      organization: item.company,
      role: item.jobTitle,
      bulletCount: item.preferredBulletCount || 5,
      currentBulletCount: item.currentBulletCount,
      maxBulletsAllowed: item.maxBulletsAllowed,
      isCurrent: item.isCurrent,
      startDate: item.startDate,
    })).sort((a, b) => {
      // Sort by current job first, then by start date
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateB - dateA;
    });
  }, [data]);

  return {
    savedPreferences,
    isLoading,
    hasPreferences: savedPreferences.length > 0,
    error,
  };
};
